import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUser, useClerk, useAuth as useClerkAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { User } from "@shared/schema";
import { identifyUser, resetAnalytics, trackEvent } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";
import { shouldShowWelcomeToast, shouldShowRefillToast } from "@/lib/credit-toast-utils";
import * as Sentry from "@sentry/react";

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  isLoggingOut: boolean;
  /** Merge a server response for the current user into the live auth user. */
  applyUserUpdate: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_TIMEOUT_MS = 8000;
const SYNC_TIMEOUT_MS = 10000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (val) => { clearTimeout(timer); resolve(val); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useClerkAuth();
  const queryClient = useQueryClient();

  const [dbUser, setDbUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [creditEvent, setCreditEvent] = useState<'welcome' | 'refill' | null>(null);
  const loadAttemptRef = useRef(0);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!clerkLoaded) return;

    if (!clerkUser) {
      setDbUser(null);
      setIsAuthLoading(false);
      return;
    }

    const attempt = ++loadAttemptRef.current;

    async function loadUser(retryCount = 0) {
      setIsAuthLoading(true);
      try {
        let token: string | null = null;
        try {
          token = await withTimeout(getTokenRef.current(), TOKEN_TIMEOUT_MS, "getToken");
        } catch {
        }

        if (attempt !== loadAttemptRef.current) return;

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const syncRes = await withTimeout(
          fetch("/api/auth/sync", {
            method: "POST",
            headers,
            credentials: "include",
          }),
          SYNC_TIMEOUT_MS,
          "auth/sync",
        );

        if (attempt !== loadAttemptRef.current) return;

        if (!syncRes.ok) {
          if (retryCount < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
            if (attempt === loadAttemptRef.current) return loadUser(retryCount + 1);
            return;
          }
          setIsAuthLoading(false);
          return;
        }

        const syncedUser = await syncRes.json();

        if (attempt !== loadAttemptRef.current) return;

        setDbUser(syncedUser);
        trackEvent("signup_completed", { userId: syncedUser.id });
        setIsAuthLoading(false);

        // Detect credit events — use state flag to avoid stale closure issues
        if (shouldShowWelcomeToast(syncedUser)) {
          setCreditEvent('welcome');
        } else if (shouldShowRefillToast(syncedUser)) {
          setCreditEvent('refill');
        }
        queryClient.setQueryData(["/api/auth/user"], syncedUser);
        identifyUser(syncedUser.id, {
          email: syncedUser.email,
          firstName: syncedUser.firstName,
        });
        Sentry.setUser({ id: syncedUser.id, email: syncedUser.email });
      } catch (err) {
        if (attempt !== loadAttemptRef.current) return;
        if (retryCount < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          if (attempt === loadAttemptRef.current) return loadUser(retryCount + 1);
          return;
        }
        setIsAuthLoading(false);
      }
    }

    loadUser();
  }, [clerkLoaded, clerkUser?.id, queryClient]);

  // Fire credit event toasts via separate effect to avoid stale closure in loadUser
  useEffect(() => {
    if (!creditEvent) return;
    if (creditEvent === 'welcome') {
      toast({ title: t('credits.welcome', 'You have 10 free credits to explore!') });
    } else if (creditEvent === 'refill') {
      toast({ title: t('credits.refillToast', 'Welcome back! Your credits have been refreshed.') });
    }
    setCreditEvent(null);
  }, [creditEvent, toast, t]);

  const isLoading = !clerkLoaded || (!!clerkUser && isAuthLoading);

  const logout = useCallback(() => {
    loadAttemptRef.current++;
    setDbUser(null);
    setIsAuthLoading(false);
    setIsLoggingOut(true);
    resetAnalytics();
    Sentry.setUser(null);
    queryClient.clear();
    signOut({ redirectUrl: "/" });
  }, [queryClient, signOut]);

  const applyUserUpdate = useCallback((patch: Partial<User>) => {
    setDbUser((prev) =>
      prev && (!patch.id || patch.id === prev.id) ? { ...prev, ...patch } : prev,
    );
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: clerkUser ? dbUser : null,
      isLoading,
      isAuthenticated: !!clerkUser && !!dbUser,
      logout,
      isLoggingOut,
      applyUserUpdate,
    }),
    [clerkUser, dbUser, isLoading, logout, isLoggingOut, applyUserUpdate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
